import { decomposeColor, Skeleton, Typography } from '@mui/material';
import Notification from '../../../../../../../../common/Styleguide/Common/Notification';
import {
  BasicLaunchMetricsSections,
  getBasicLaunchData,
  getFormattedDate,
  isBeforeToday,
} from '../../../../../../../helpers/metricsHelpers';
import { useQuery } from '@apollo/client';
import { BASIC_LAUNCH_GAME_STATS_QUERY, BasicLaunchGameStatsQuery } from '../../../submission-graphql';
import React from 'react';
import { GaugeCards } from '../../../../../../../../common/Styleguide/Common/gaugeCard.styles';
import GaugeCard from '../../../../../../../../common/Styleguide/Common/GaugeCard';
import MetricSectionTitle from '../../../../../../../../common/Styleguide/Common/MetricSectionTitle';
import { COLORS } from '../../../../../../../../common/Styleguide/Common/colors';
import moment from 'moment';

const DATE_SKELETON_SX = {
  display: 'inline-block',
  width: '100px',
  fontSize: '14px',
  bgcolor: `rgba(${decomposeColor(COLORS.brand[40]).values},.5)`,
  verticalAlign: 'bottom',
};

type SubmissionTabMetricsBasicLaunchProps = {
  slug: string;
  categoryName: string;
  basicLaunchOn: string | null;
  basicLaunchExpiresOn: string | null;
};

const SubmissionTabMetricsBasicLaunch: React.FC<SubmissionTabMetricsBasicLaunchProps> = ({
  slug,
  categoryName,
  basicLaunchOn,
  basicLaunchExpiresOn,
}) => {
  const { loading: loadingBasicLaunchData, data: rawBasicLaunchData } = useQuery<BasicLaunchGameStatsQuery>(BASIC_LAUNCH_GAME_STATS_QUERY, {
    variables: { slug: slug },
  });
  const basicLaunchData =
    rawBasicLaunchData?.gameBySlug.basicLaunchStatsAndBenchmarks &&
    getBasicLaunchData(rawBasicLaunchData.gameBySlug.basicLaunchStatsAndBenchmarks);
  const basicLaunchExpired = isBeforeToday(basicLaunchExpiresOn);

  const SECTIONS: BasicLaunchMetricsSections[] = [
    {
      id: 'desktop',
      title: 'Desktop',
      prognosis: {
        min: 0,
        max: 15,
        levels: [
          {
            min: 0,
            color: 'worst',
            label: 'Below requirements (<9)',
          },
          {
            min: 9,
            color: 'middle',
            label: 'Pending manual review (9, 10)',
          },
          {
            min: 11,
            color: 'best',
            label: 'Ready for Full Launch (>10)',
          },
        ],
      },
      totalScore: basicLaunchData?.desktop?.totalScore,
      metrics: [
        {
          id: 'averagePlaytime',
          title: 'Average Playtime',
          tooltip: 'Average time played per play, excluding time away from the game (counted in minutes and seconds).',
        },
        {
          id: 'retentionD1',
          title: 'Retention (D1)',
          tooltip: 'The ratio of users that play the game again 1 day after they played it for the first time.',
        },
        {
          id: 'gameplayConversion',
          title: 'Gameplay Conversion',
          tooltip:
            "Ratio of plays with a duration of at least 1 min compared to the clicks on 'Play Now' button.<br>Pro Tip: Reduce the initial loading size and simplify the flow to start gameplay in order to improve this ratio and lose fewer players early.",
        },
      ],
    },
    {
      id: 'mobile',
      title: 'Mobile and Tablet',
      prognosis: {
        notes: 'only if it passes Desktop',
        min: 0,
        max: 15,
        levels: [
          {
            min: 0,
            color: 'worst',
            label: 'Disabled on Mobile and Tablet (<9)',
          },
          {
            min: 9,
            color: 'middle',
            label: 'Enabled, but improvements recommended (9, 10)',
          },
          {
            min: 11,
            color: 'best',
            label: 'Enabled on Mobile and Tablet (>10)',
          },
        ],
      },
      totalScore: basicLaunchData?.mobile?.totalScore,
      metrics: [
        {
          id: 'averagePlaytime',
          title: 'Average Playtime',
          tooltip: 'Average time played per play, excluding time away from the game (counted in minutes and seconds).',
        },
        {
          id: 'retentionD1',
          title: 'Retention (D1)',
          tooltip: 'The ratio of users that play the game again 1 day after they played it for the first time.',
        },
        {
          id: 'gameplayConversion',
          title: 'Gameplay Conversion',
          tooltip:
            "Ratio of plays with a duration of at least 1 min compared to the clicks on 'Play Now' button.<br>Pro Tip: Reduce the initial loading size and simplify the flow to start gameplay in order to improve this ratio and lose fewer players early.",
        },
      ],
    },
  ];

  if (!loadingBasicLaunchData && rawBasicLaunchData?.gameBySlug?.basicLaunchStatsAndBenchmarks === null) {
    return <Notification extraSx={{ mt: '20px' }} theme="warning" description={<>Try again later.</>} />;
  }

  return (
    <>
      <Notification
        theme="success"
        extraSx={{ mt: '20px' }}
        description={
          <>
            <Typography variant="body2">
              Basic Launch Period:{' '}
              {loadingBasicLaunchData ? (
                <Skeleton variant="text" sx={DATE_SKELETON_SX} animation="wave" />
              ) : (
                <strong>{getFormattedDate(basicLaunchOn, 'DD/MM/YYYY')}</strong>
              )}{' '}
              -{' '}
              {loadingBasicLaunchData ? (
                <Skeleton variant="text" sx={DATE_SKELETON_SX} animation="wave" />
              ) : (
                <strong>{getFormattedDate(basicLaunchExpiresOn, 'DD/MM/YYYY')}</strong>
              )}
            </Typography>
            <Typography variant="body2">
              Evaluation Period:{' '}
              {loadingBasicLaunchData ? (
                <Skeleton variant="text" sx={DATE_SKELETON_SX} animation="wave" />
              ) : (
                <strong>
                  {getFormattedDate(moment.utc(basicLaunchExpiresOn).startOf('day').subtract(7, 'day').toString(), 'DD/MM/YYYY')}
                </strong>
              )}{' '}
              -{' '}
              {loadingBasicLaunchData ? (
                <Skeleton variant="text" sx={DATE_SKELETON_SX} animation="wave" />
              ) : (
                <strong>{getFormattedDate(basicLaunchExpiresOn, 'DD/MM/YYYY')}</strong>
              )}
            </Typography>
          </>
        }
      />
      <Notification
        theme="info"
        description={
          <Typography variant="body2">
            Benchmarks for calculating scores are based on games in the category <strong>{categoryName}</strong> in the period{' '}
            {loadingBasicLaunchData ? (
              <Skeleton variant="text" sx={DATE_SKELETON_SX} animation="wave" />
            ) : (
              <strong>{getFormattedDate(basicLaunchOn, 'prev3_MMM-MMM_YYYY')}</strong>
            )}
            .
            <br />
            The last 7 days of your basic launch period are used to compute your game's stats and the scores recorded when basic launch
            expires are final.
          </Typography>
        }
        extraSx={{ mt: 2, mb: 5 }}
      />
      {SECTIONS.map((section, i) => {
        if (!basicLaunchData?.[section.id]) {
          return null;
        }

        return (
          <section key={i} style={{ marginBottom: 48 }}>
            <MetricSectionTitle title={section.title} hasScoring prognosis={section.prognosis} score={section.totalScore} />
            <GaugeCards>
              {section.metrics.map((metric, i) => {
                return (
                  <GaugeCard
                    key={i}
                    title={metric.title}
                    tooltip={metric.tooltip}
                    total={basicLaunchData?.[section.id]?.[metric.id]?.total}
                    score={basicLaunchData?.[section.id]?.[metric.id]?.score}
                    nextScore={!basicLaunchExpired ? basicLaunchData?.[section.id]?.[metric.id]?.nextScore : ''}
                  />
                );
              })}
            </GaugeCards>
          </section>
        );
      })}
    </>
  );
};

export default SubmissionTabMetricsBasicLaunch;
